/**
 * LANGUAGE LINKS — `/<language>`, e.g. saysomethingin.app/welsh (job #297).
 *
 * Aran, 2026-11-04: visiting /welsh dropped a stranger on the course picker
 * (/start). A link that names a language has already made the choice the
 * picker asks for, so it goes straight to the player with that language's
 * course on, exactly as if the visitor had pressed it on /start.
 *
 * Two halves, the same shape as the `?script=<lang>` share (script/shareLink):
 *
 *  1. At boot, before the router (main.js), `/welsh?utm_x=1` is rewritten in
 *     place to `/?lang=cym&utm_x=1`. The router's own catch-all would
 *     otherwise send `/welsh` to a bare `/` — and the picker. The `lang` key
 *     is not one pickerFirst counts as harmless, so the picker stands aside.
 *  2. Once the catalogue is in, App turns `lang` into ONE course code with
 *     courseForLanguage() below and hands it to the `?course=` link path, so
 *     the entitlement gate, the remembered course and the signed-in learner's
 *     saved course all move exactly as a course link moves them.
 *
 * Which course: the visitor's "I speak" (`ssi-i-speak`), English when they have
 * none — the same default /start gives a stranger — and English again when the
 * language has no course for what they speak. /start has no default variant,
 * so a language offered in several courses takes its plain `<lang>_for_<known>`
 * course where the catalogue has one (spa_for_eng, not spa_mx_for_eng), and
 * otherwise the variant /start lists FIRST, the one the picker already treats
 * as the likely tap when a language row is opened (Welsh: North, cym_n_for_eng,
 * because the live catalogue has no plain cym_for_eng and both variants carry
 * target_lang 'cym', so they keep the catalogue's display_name order).
 *
 * The router needs its own record too (router/index.ts 'language-link'): its
 * history is created when its module is imported, before main.js runs, so its
 * first navigation still reads /welsh and must carry `lang` across itself.
 *
 * An unknown slug is never rewritten, so it keeps today's landing. A known
 * language with no course at all is handled by App like an unknown course
 * code: a stranger gets the picker page, anyone else keeps their course.
 */

/** Slug → the catalogue's base target language (variant suffixes stripped). */
export const LANGUAGE_SLUGS: Readonly<Record<string, string>> = Object.freeze({
  // Welsh, in English and in Welsh.
  welsh: 'cym', cymraeg: 'cym',
  cornish: 'cor', kernewek: 'cor',
  breton: 'bre', brezhoneg: 'bre',
  irish: 'gle', gaeilge: 'gle',
  'scottish-gaelic': 'gla', gaelic: 'gla', gaidhlig: 'gla',
  manx: 'glv',
  spanish: 'spa', espanol: 'spa', 'español': 'spa',
  french: 'fra', francais: 'fra', 'français': 'fra',
  german: 'deu', deutsch: 'deu',
  italian: 'ita', italiano: 'ita',
  portuguese: 'por', portugues: 'por', 'português': 'por',
  dutch: 'nld', nederlands: 'nld',
  chinese: 'zho', mandarin: 'cmn', cantonese: 'yue',
  japanese: 'jpn', korean: 'kor', arabic: 'ara',
  basque: 'eus', euskara: 'eus',
  catalan: 'cat', catala: 'cat', 'català': 'cat',
  galician: 'glg', galego: 'glg',
  afrikaans: 'afr', bengali: 'ben', bulgarian: 'bul', croatian: 'hrv', czech: 'ces',
  danish: 'dan', finnish: 'fin', greek: 'ell', hebrew: 'heb', hindi: 'hin',
  hungarian: 'hun', icelandic: 'isl', indonesian: 'ind', latvian: 'lav',
  lithuanian: 'lit', malay: 'msa', maltese: 'mlt', norwegian: 'nor', persian: 'fas',
  polish: 'pol', punjabi: 'pan', romanian: 'ron', russian: 'rus', serbian: 'srp',
  slovak: 'slk', slovenian: 'slv', swahili: 'swa', swedish: 'swe', tamil: 'tam',
  telugu: 'tel', thai: 'tha', turkish: 'tur', ukrainian: 'ukr', urdu: 'urd',
  vietnamese: 'vie', yiddish: 'yid', yoruba: 'yor', zulu: 'zul',
  gujarati: 'guj', kannada: 'kan', marathi: 'mar', nepali: 'nep', sinhala: 'sin',
  english: 'eng',
})

export const LANGUAGE_LINK_PARAM = 'lang'

/** cym_n → cym, cym_s → cym: the same folding the picker groups variants by. */
export function baseLang(code: string | null | undefined): string {
  return (code || '').replace(/_(n|s|north|south|latam)$/i, '')
}

/** A single-segment path naming a language we know, or null. */
export function languageOfPath(pathname: string): string | null {
  const m = /^\/([^/]+)\/?$/.exec(pathname || '')
  if (!m) return null
  let slug = ''
  try {
    slug = decodeURIComponent(m[1]).trim().toLowerCase()
  } catch {
    return null
  }
  return Object.prototype.hasOwnProperty.call(LANGUAGE_SLUGS, slug) ? LANGUAGE_SLUGS[slug] : null
}

/**
 * The address a `/<language>` path stands for, or null when it is not one.
 * Every other query key (utm tags, referrers) rides along untouched; a
 * `?course=` the link already carries is more specific and is left to win.
 */
export function languageLinkTarget(pathname: string, search: string): string | null {
  const lang = languageOfPath(pathname)
  if (!lang) return null
  const params = new URLSearchParams(search || '')
  params.set(LANGUAGE_LINK_PARAM, lang)
  return `/?${params.toString()}`
}

/** Rewrite the current address in place when it is a language link. Called once from main.js. */
export function rewriteLanguageLink(loc: Location = window.location, hist: History = window.history): boolean {
  const target = languageLinkTarget(loc.pathname, loc.search)
  if (!target) return false
  hist.replaceState(hist.state, '', target + (loc.hash || ''))
  return true
}

/** The language a boot address asks for (`?lang=cym`), or null. */
export function languageFromAddress(search: string): string | null {
  try {
    const v = new URLSearchParams(search || '').get(LANGUAGE_LINK_PARAM)
    return v ? v.trim().toLowerCase() : null
  } catch {
    return null
  }
}

export interface CatalogueCourse {
  course_code: string
  target_lang?: string | null
  known_lang?: string | null
}

/**
 * The course a language link opens, or null when the catalogue has none.
 *
 * `nameOf` is the picker's own language-name reader (getLanguageName), so the
 * variants come out in the order /start lists them and the first is taken.
 */
export function courseForLanguage(
  catalogue: CatalogueCourse[],
  lang: string,
  iSpeak: string | null | undefined,
  nameOf: (targetLang: string) => string = (t) => t,
): string | null {
  const want = baseLang(lang)
  const ofLang = catalogue.filter((c) => c?.course_code && baseLang(c.target_lang) === want)
  const pick = (known: string) => ofLang.filter((c) => c.known_lang === known)
  let list = iSpeak ? pick(iSpeak) : []
  if (list.length === 0) list = pick('eng')
  if (list.length === 0) return null
  // The language's own unsuffixed course (spa_for_eng beside spa_mx_for_eng)
  // is the nearest thing the catalogue has to a default, so it wins.
  const known = list[0].known_lang
  const plain = list.find((c) => c.course_code === `${want}_for_${known}`)
  if (plain) return plain.course_code
  const sorted = [...list].sort((a, b) => nameOf(a.target_lang || '').localeCompare(nameOf(b.target_lang || '')))
  return sorted[0].course_code
}
